import { AkSpecialArea } from './akTypes';
import type { BccRiskZone } from './bodyMap';

/**
 * Hartă a regiunilor feței (cap + gât) pentru localizarea leziunilor / câmpurilor AK.
 *
 * Codurile sunt stabile (persistate în DB). `bccZone` reflectă zonele de risc
 * pentru carcinomul bazocelular (§17.1):
 * - **Zona H** — periorbital, nazal, auricular, temple, buze;
 * - **Zona M** — frunte, bărbie, mandibulă, scalp, gât.
 */

export interface FaceRegion {
  /** Cod stabil al regiunii (persistat în DB). */
  code: string;
  /** Etichetă în română. */
  labelRo: string;
  /** Zona de risc BCC (§17.1). */
  bccZone: BccRiskZone;
  /** Zonă specială AK asociată regiunii, dacă există. */
  akSpecialArea?: AkSpecialArea;
}

export const FACE_REGIONS: FaceRegion[] = [
  { code: 'FRONTAL_SUPERIOR', labelRo: 'Frontal superior (frunte)', bccZone: 'M' },
  { code: 'TEMPORAL', labelRo: 'Temporal (temple)', bccZone: 'H' },
  { code: 'CHEEK', labelRo: 'Obaz (malar)', bccZone: 'H' },
  { code: 'NASAL', labelRo: 'Nazal (nas)', bccZone: 'H' },
  { code: 'PERIORBITAL', labelRo: 'Periorbital (pleoape)', bccZone: 'H' },
  { code: 'LIPS', labelRo: 'Buze (buză superioară și inferioară)', bccZone: 'H', akSpecialArea: AkSpecialArea.LOWER_LIP },
  { code: 'CHIN', labelRo: 'Bărbie (menton)', bccZone: 'M' },
  { code: 'JAW', labelRo: 'Mandibulă / linia mandibulară', bccZone: 'M' },
  { code: 'EARS', labelRo: 'Urechi (pavilion auricular)', bccZone: 'H', akSpecialArea: AkSpecialArea.EARRS },
  { code: 'SCALP', labelRo: 'Scalp (vertex și temporoparietal)', bccZone: 'M' },
  { code: 'NECK', labelRo: 'Gât (cervical)', bccZone: 'M' },
];

/** Găsește o regiune facială după cod. */
export const faceRegionByCode = (code: string | null | undefined): FaceRegion | undefined =>
  FACE_REGIONS.find((r) => r.code === code);

/** Eticheta RO a regiunii faciale (sau `—` dacă regiunea nu e cunoscută). */
export const faceRegionLabel = (code: string | null | undefined): string =>
  faceRegionByCode(code)?.labelRo ?? '—';
